// Tool-approval transport — carved from `src/features/tool-approvals/api.ts`
// (ARCFE-1059 · D1). Transport comes from the `@heidi/api` configured-client
// registry (E5, ARCFE-805).
//
// THE DOUBLE ROUTE DECLARATION IS HEALED HERE, which is the point of carving
// this module in the same PR as the harness contracts. Before this change the
// SAME two backend routes were spelled twice:
//
//   src/features/tool-approvals/api.ts   inline template literals
//   src/features/agent-harness/paths.ts  `agentHarnessRunApprovalsPath` /
//                                        `agentHarnessRunApprovalResolvePath`
//
// One route, two definitions — the route-level form of the duplicate key
// literals the program has been finding elsewhere. `paths.ts` is now the single
// declaration site (it moved to `/contracts`, where stable identifiers belong)
// and this module imports the builders instead of re-spelling them.
//
// The emitted paths are byte-identical to the pre-move literals: both builders
// `encodeURIComponent` each segment, exactly as the inline template did, and
// `paths.test.ts` pins the encoding.
import { getScribeHttpClients } from '@heidi/api/client';

import {
  agentHarnessRunApprovalResolvePath,
  agentHarnessRunApprovalsPath,
} from '../../contracts/paths';
import {
  normalizeToolApprovalPublic,
  pendingApprovalsResponseSchema,
  type PendingToolApproval,
  type ResolveToolApprovalRequest,
  type ResolveToolApprovalResponse,
} from '../../contracts/tool-approval.types';

interface ScribeHttp {
  get<T>(
    url: string,
    config?: { params?: Record<string, unknown> }
  ): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
}

const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

export const fetchPendingToolApprovals = async (
  agentRunId: string
): Promise<PendingToolApproval[]> => {
  const res = await http().get<unknown>(agentHarnessRunApprovalsPath(agentRunId), {
    params: { status: 'pending' },
  });
  const parsed = pendingApprovalsResponseSchema.parse(res.data);
  return parsed.approvals.map(normalizeToolApprovalPublic);
};

export const fetchPendingToolApproval = async (
  agentRunId: string,
  approvalId: string
): Promise<PendingToolApproval> => {
  const approvals = await fetchPendingToolApprovals(agentRunId);
  const approval = approvals.find((candidate) => candidate.approvalId === approvalId);
  if (!approval) {
    throw new Error(
      `Pending approval ${approvalId} was not found for run ${agentRunId}.`
    );
  }
  return approval;
};

export const resolveToolApproval = async (
  approval: Pick<PendingToolApproval, 'agentRunId' | 'approvalId'>,
  body: ResolveToolApprovalRequest
): Promise<ResolveToolApprovalResponse> => {
  const res = await http().post<ResolveToolApprovalResponse>(
    agentHarnessRunApprovalResolvePath(approval.agentRunId, approval.approvalId),
    body
  );
  return res.data;
};
